import { ReceptionApiAdapter } from '../adapters/ReceptionApiAdapter';
import {
  canDeleteReception,
  canIdentifyPackage,
  canSplitPackage,
  CreateReceptionRequest,
  DestinationType,
  getRequiredPayloadFields,
  IdentifyPackageRequest,
  Package,
  Reception,
  ReceptionFilters,
  ReceptionListItem,
  ReceptionPage,
  SplitPackageRequest,
  UpdateReceptionRequest,
} from '../models/Reception';

/**
 * Servicio de Recepciones
 *
 * Capa de lógica de negocio que valida datos antes de enviarlos al adapter.
 */
export class ReceptionService {
  constructor(private apiAdapter: ReceptionApiAdapter) {}

  // ============================================================================
  // RECEPCIONES - CONSULTAS
  // ============================================================================

  /**
   * Obtiene lista paginada de recepciones
   */
  async getReceptions(filters: ReceptionFilters = {}): Promise<ReceptionPage> {
    // Validar per_page
    if (filters.per_page && filters.per_page > 100) {
      throw new Error('El límite por página no puede ser mayor a 100');
    }

    return this.apiAdapter.getReceptions(filters);
  }

  /**
   * Obtiene todas las recepciones
   */
  async getAllReceptions(filters: ReceptionFilters = {}): Promise<ReceptionListItem[]> {
    return this.apiAdapter.getAllReceptions(filters);
  }

  /**
   * Obtiene el detalle de una recepción
   */
  async getReception(id: number): Promise<Reception> {
    if (!id || id <= 0) {
      throw new Error('ID de recepción inválido');
    }
    return this.apiAdapter.getReception(id);
  }

  // ============================================================================
  // RECEPCIONES - MUTACIONES
  // ============================================================================

  /**
   * Crea una nueva recepción
   */
  async createReception(data: CreateReceptionRequest): Promise<Reception> {
    // Validar campos requeridos
    if (!data.carrier?.trim()) {
      throw new Error('El transportista es obligatorio');
    }
    if (!data.vehicle_plate?.trim()) {
      throw new Error('La matrícula del vehículo es obligatoria');
    }
    if (!data.driver_name?.trim()) {
      throw new Error('El nombre del conductor es obligatorio');
    }

    // Validar paquetes
    if (!data.packages || data.packages.length === 0) {
      throw new Error('Debe incluir al menos un paquete');
    }

    // Validar cada paquete
    for (const pkg of data.packages) {
      if (!pkg.package_type) {
        throw new Error('Cada paquete debe tener un tipo');
      }
      if (pkg.package_type === 'OTHER' && !pkg.package_type_other?.trim()) {
        throw new Error('Debe especificar el tipo para paquetes de tipo "Otro"');
      }
      if (!pkg.quantity || pkg.quantity < 1) {
        throw new Error('La cantidad debe ser al menos 1');
      }
    }

    return this.apiAdapter.createReception({
      ...data,
      carrier: data.carrier.trim(),
      vehicle_plate: data.vehicle_plate.trim(),
      driver_name: data.driver_name.trim(),
      ...(data.signature_ref?.trim() && { signature_ref: data.signature_ref.trim() }),
      ...(data.signature_file && { signature_file: data.signature_file }),
    });
  }

  /**
   * Actualiza una recepción existente
   */
  async updateReception(id: number, data: UpdateReceptionRequest, currentStatus: string): Promise<Reception> {
    const isClosedReception = currentStatus === 'CLOSED';

    // Validar campos requeridos
    if (!data.carrier?.trim()) {
      throw new Error('El transportista es obligatorio');
    }
    if (!data.vehicle_plate?.trim()) {
      throw new Error('La matrícula del vehículo es obligatoria');
    }
    if (!data.driver_name?.trim()) {
      throw new Error('El nombre del conductor es obligatorio');
    }
    if (!data.signature_ref?.trim()) {
      throw new Error('La referencia de firma es obligatoria');
    }

    // Validar paquetes a añadir
    if (data.packages_to_add) {
      for (const pkg of data.packages_to_add) {
        if (!pkg.package_type) {
          throw new Error('Cada paquete debe tener un tipo');
        }
        if (pkg.package_type === 'OTHER' && !pkg.package_type_other?.trim()) {
          throw new Error('Debe especificar el tipo para paquetes de tipo "Otro"');
        }
        if (!pkg.quantity || pkg.quantity < 1) {
          throw new Error('La cantidad debe ser al menos 1');
        }
      }
    }

    // Validar paquetes a actualizar (no aplica para recepciones cerradas)
    if (!isClosedReception && data.packages_to_update) {
      for (const pkg of data.packages_to_update) {
        if (!pkg.id) {
          throw new Error('Los paquetes a actualizar deben tener ID');
        }
        if (!pkg.package_type) {
          throw new Error('Cada paquete debe tener un tipo');
        }
        if (pkg.package_type === 'OTHER' && !pkg.package_type_other?.trim()) {
          throw new Error('Debe especificar el tipo para paquetes de tipo "Otro"');
        }
        if (!pkg.quantity || pkg.quantity < 1) {
          throw new Error('La cantidad debe ser al menos 1');
        }
      }
    }

    const payload: UpdateReceptionRequest = {
      ...data,
      carrier: data.carrier.trim(),
      vehicle_plate: data.vehicle_plate.trim(),
      driver_name: data.driver_name.trim(),
      signature_ref: data.signature_ref.trim(),
    };

    // En estado cerrado solo se permite agregar paquetes
    if (isClosedReception) {
      delete payload.packages_to_update;
      delete payload.package_ids_to_delete;
    }

    return this.apiAdapter.updateReception(id, payload);
  }

  /**
   * Elimina una recepción
   */
  async deleteReception(reception: Reception | ReceptionListItem): Promise<void> {
    if (!reception.id || reception.id <= 0) {
      throw new Error('ID de recepción inválido');
    }

    if (!canDeleteReception(reception)) {
      throw new Error('Solo se pueden eliminar recepciones sin identificar y sin paquetes identificados');
    }

    return this.apiAdapter.deleteReception(reception.id);
  }

  // ============================================================================
  // PAQUETES - OPERACIONES
  // ============================================================================

  /**
   * Identifica un paquete
   */
  async identifyPackage(
    receptionId: number,
    pkg: Package,
    receptionStatus: string,
    data: IdentifyPackageRequest,
  ): Promise<Reception> {
    // Validar que el paquete puede ser identificado
    if (!canIdentifyPackage(pkg, receptionStatus as Reception['status'])) {
      if (pkg.status === 'IDENTIFIED') {
        throw new Error('El paquete ya ha sido identificado');
      }
      throw new Error('No se puede identificar el paquete en una recepción cerrada');
    }

    // Validar tipo de destino
    if (!data.destination_type) {
      throw new Error('El tipo de destino es obligatorio');
    }

    // Validar payload según tipo de destino
    const requiredFields = getRequiredPayloadFields(data.destination_type);
    for (const field of requiredFields) {
      if (!data.payload || !(field in data.payload) || !data.payload[field as keyof typeof data.payload]) {
        throw new Error(`El campo "${field}" es obligatorio para el destino ${data.destination_type}`);
      }
    }

    return this.apiAdapter.identifyPackage(receptionId, pkg.id, data);
  }

  /**
   * Divide un paquete
   */
  async splitPackage(
    receptionId: number,
    pkg: Package,
    receptionStatus: string,
    data: SplitPackageRequest,
  ): Promise<Reception> {
    // Validar que el paquete puede ser dividido
    if (!canSplitPackage(pkg)) {
      if (pkg.status === 'IDENTIFIED') {
        throw new Error('No se puede dividir un paquete ya identificado');
      }
      if (pkg.quantity <= 1) {
        throw new Error('El paquete debe tener más de 1 unidad para poder dividirlo');
      }
    }

    // Validar cantidad de split
    if (!data.split_qty || data.split_qty <= 0) {
      throw new Error('La cantidad a separar debe ser mayor a 0');
    }
    if (data.split_qty >= pkg.quantity) {
      throw new Error(`La cantidad a separar debe ser menor a ${pkg.quantity}`);
    }

    return this.apiAdapter.splitPackage(receptionId, pkg.id, data);
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Obtiene las etiquetas traducidas para un tipo de destino
   */
  getDestinationTypeLabel(type: DestinationType): string {
    const labels: Record<DestinationType, string> = {
      INBOUND: 'Entrada de mercancía',
      REJECTED: 'Rechazado',
      RETURN: 'Devolución',
      SUPPLY: 'Suministros',
    };
    return labels[type] || type;
  }
}
